import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { take } from 'lib/array/arrayUtils'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { rem } from 'polished'
import CalendarSidebarDaysMonth from './CalendarSidebarDaysMonth'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import CalendarPriceBlurb from './CalendarPriceBlurb'
import Group from 'components/utils/Group'
import { useIntl } from 'react-intl'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { getDateFromMonthKey, useIntlDateFormatter } from 'lib/datetime/dateUtils'

const Root = styled.div`
  width: 100%;
`

const Controls = styled.div`
  display: flex;

  > * + * {
    margin-inline-start: ${rem(12)};
  }
`

const NextMontControls = styled(Controls)`
  margin-top: ${rem(16)};
`

const Calendar = styled.div`
  & + & {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

interface Props {
  className?: string
  onDatesChange: (day: App.BedbankCalendarDay) => void
  onBackToMonth: () => void
  monthIndex: string
  duration: number
  flights: boolean
  daysByMonth: Array<App.BedbankCalendarMonth>
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarSidebarDayPicker(props: Props) {
  const {
    className,
    monthIndex,
    daysByMonth,
    onDatesChange,
    onBackToMonth,
    duration,
    flights,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const [monthsOnDisplay, setMonthsOnDisplay] = useState(2)
  const [monthOffset, setMonthOffset] = useState(daysByMonth.findIndex((entry) => entry.key === monthIndex))

  const onPrevious = useCallback(() => {
    setMonthOffset(monthOffset - 1)
    setMonthsOnDisplay(monthsOnDisplay + 1)
  }, [monthOffset, monthsOnDisplay])

  const onNext = useCallback(() => {
    setMonthsOnDisplay(monthsOnDisplay + 1)
  }, [monthsOnDisplay])

  const keyData = flights ? 'cheapestDayWithFlights' : 'cheapestDay'

  const previousMonthLabel = intl.formatMessage({
    id: 'wzAtSeVdhjO1ohNeIgWY',
    defaultMessage: 'Previous month',
    description: 'Button to navigate to the previous month in the calendar',
  })
  const nextMonthLabel = intl.formatMessage({
    id: 'lvDLlZ4bNzn-aJ-jgWVC',
    defaultMessage: 'Next month',
    description: 'Button to navigate to the next month in the calendar',
  })
  const returnToYearlyCalendarLabel = intl.formatMessage({
    id: 'IFQWq24v1tmPc-R1Od8m',
    defaultMessage: 'Return to Yearly Calendar',
    description: 'Aria label for button to return to the yearly calendar view',
  })

  return (
    <Root className={className}>
      {take(daysByMonth, monthsOnDisplay, monthOffset).map((value, index) =>
        <Calendar key={value.key}>
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <BodyText variant="large" align="center" weight="bold">{dateFormatter(getDateFromMonthKey(value.key), INTL_LONG_MONTH_LONG_YEAR)}</BodyText>
            {index === 0 && <Controls>
              <TextButton
                kind="secondary"
                disabled={monthOffset === 0}
                onClick={onPrevious}
              >
                {previousMonthLabel}
              </TextButton>
              <IconButton
                kind="secondary"
                onClick={onBackToMonth}
                aria-label={returnToYearlyCalendarLabel}
              >
                <LineCalendarIcon />
              </IconButton>
            </Controls>}
          </Group>
          {!!value[keyData] && <CalendarPriceBlurb
            price={value[keyData]?.price}
            propertyFees={value[keyData]?.propertyFees}
            flightPrice={value[keyData]?.flightPrice}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
          />}
          <CalendarSidebarDaysMonth
            flights={flights}
            days={value.days}
            onDatesChange={onDatesChange}
            duration={duration}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
            roomsNumber={roomsNumber}
          />
        </Calendar>,
      )}
      <NextMontControls>
        <TextButton
          kind="secondary"
          disabled={(monthsOnDisplay + monthOffset) >= daysByMonth.length}
          onClick={onNext}
        >
          {nextMonthLabel}
        </TextButton>
        <IconButton
          kind="secondary"
          onClick={onBackToMonth}
          aria-label={returnToYearlyCalendarLabel}
        >
          <LineCalendarIcon />
        </IconButton>
      </NextMontControls>
    </Root>
  )
}

export default CalendarSidebarDayPicker
